// Phase 5 (web revival) — Stream D.
//
// v1 crop UI: four number inputs. NOT the visual drag-handle overlay
// that lives in v2 — see Edit.tsx comments for why we shipped this
// simpler form first (server validates the same payload either way, and
// 90% of the editing value lands with sliders + filters, not the crop
// box).

import { useId } from 'react';

export interface CropValue {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface CropOverlayProps {
  value: CropValue | null;
  onChange: (next: CropValue | null) => void;
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

function isFullFrame(c: CropValue): boolean {
  return c.x <= 0.001 && c.y <= 0.001 && c.w >= 0.999 && c.h >= 0.999;
}

function valid(c: CropValue): boolean {
  return (
    c.x >= 0 &&
    c.y >= 0 &&
    c.w > 0 &&
    c.h > 0 &&
    c.x + c.w <= 1.001 &&
    c.y + c.h <= 1.001
  );
}

export function CropOverlay({ value, onChange }: CropOverlayProps) {
  const current: CropValue = value ?? { x: 0, y: 0, w: 1, h: 1 };
  const isValid = valid(current);

  function update(patch: Partial<CropValue>) {
    const next: CropValue = {
      x: clamp01(patch.x ?? current.x),
      y: clamp01(patch.y ?? current.y),
      w: clamp01(patch.w ?? current.w),
      h: clamp01(patch.h ?? current.h),
    };
    if (isFullFrame(next)) {
      onChange(null);
    } else {
      onChange(next);
    }
  }

  function reset() {
    onChange(null);
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
          Crop
        </h3>
        <button
          type="button"
          onClick={reset}
          disabled={value === null}
          className="text-xs text-neutral-400 hover:text-neutral-100 disabled:opacity-40"
        >
          Reset
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <CropField label="x" value={current.x} onChange={(v) => update({ x: v })} />
        <CropField label="y" value={current.y} onChange={(v) => update({ y: v })} />
        <CropField label="w" value={current.w} onChange={(v) => update({ w: v })} />
        <CropField label="h" value={current.h} onChange={(v) => update({ h: v })} />
      </div>
      {!isValid && (
        <p className="mt-2 text-xs text-amber-300">
          Crop out of range — x+w and y+h must be ≤ 1.
        </p>
      )}
    </div>
  );
}

function CropField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  const id = useId();
  return (
    <label htmlFor={id} className="flex items-center gap-2 text-xs text-neutral-300">
      <span className="w-4 text-neutral-500">{label}</span>
      <input
        id={id}
        type="number"
        min={0}
        max={1}
        step={0.01}
        value={Number.isFinite(value) ? Number(value.toFixed(3)) : 0}
        onChange={(e) => {
          const n = Number.parseFloat(e.target.value);
          onChange(Number.isFinite(n) ? n : 0);
        }}
        className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-2 py-1 text-xs text-neutral-100 focus-visible:border-neutral-600 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-600"
      />
    </label>
  );
}
